import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";

// ============================================================
// Circular crop modal. Outputs a SQUARE base64 JPEG cropped to
// the user's selection. Compression happens upstream in
// PhotoUploader.compressImageDataUrl.
// ============================================================

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.src = url;
  });
}

async function getCroppedDataUrl(sourceUrl, area) {
  const image = await createImage(sourceUrl);
  const canvas = document.createElement("canvas");
  // Cap at 800 px so the downstream compressor has reasonable input.
  const size = Math.min(Math.round(area.width), 800);
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, size, size);
  return canvas.toDataURL("image/jpeg", 0.92);
}

export default function PhotoCropModal({ src, onCancel, onCropped }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixelArea, setPixelArea] = useState(null);
  const [busy, setBusy] = useState(false);

  const onComplete = useCallback((_, p) => setPixelArea(p), []);

  const confirm = async () => {
    if (!pixelArea) return;
    setBusy(true);
    try {
      const out = await getCroppedDataUrl(src, pixelArea);
      onCropped(out);
    } catch (e) {
      console.error("crop failed", e);
      alert("Could not crop the image. Please try a different file.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="photo-crop-modal"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)",
        zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        style={{
          background: "var(--kc-surface)",
          border: "1px solid color-mix(in srgb, var(--kc-accent) 45%, transparent)",
          borderRadius: 14,
          width: "100%", maxWidth: 420,
          color: "#fff", fontFamily: "'Montserrat',sans-serif",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}
      >
        <div style={{ padding: "14px 18px", borderBottom: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", fontFamily: "'Cinzel',serif", fontSize: 13, fontWeight: 700, color: "var(--kc-accent)", textTransform: "uppercase", letterSpacing: "0.10em" }}>
          Crop Photo
        </div>
        <div style={{ position: "relative", width: "100%", height: 320, background: "#000" }}>
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onComplete}
          />
        </div>
        <div style={{ padding: "12px 18px", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 11, color: "var(--kc-sub)" }}>Zoom</span>
          <input
            type="range"
            min={1}
            max={4}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            data-testid="photo-crop-zoom"
            style={{ flex: 1, accentColor: "var(--kc-accent)" }}
          />
        </div>
        <div style={{ padding: "12px 18px 18px", display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            type="button"
            data-testid="photo-crop-cancel"
            onClick={onCancel}
            disabled={busy}
            className="btn btn-outline btn-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="photo-crop-save"
            onClick={confirm}
            disabled={busy || !pixelArea}
            className="btn btn-gold btn-sm"
          >
            {busy ? "Cropping…" : "Crop & Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
